import React from 'react';
import { motion } from 'framer-motion';
import { Users, TrendingUp, Award } from 'lucide-react';

const ResultsSection = () => {
  // Define a common animation for the images (rolling)
  const imageAnimation = {
    rotateY: 360, // Rotate around the Y-axis for a left-to-right roll
  };

  // Define a common transition for the images (rolling)
  const imageTransition = {
    duration: 15, // 15 seconds for one full rotation (slow)
    ease: "linear", // Constant speed
    repeat: Infinity, // Loop indefinitely
  };

  // Define colors for the image's neon glow effect
  const imageNeonColors = [
    "rgba(0, 255, 255, 0.5)", // Turquoise
    "rgba(255, 0, 255, 0.5)", // Magenta
    "rgba(255, 255, 0, 0.5)", // Yellow
    "rgba(0, 255, 0, 0.5)",   // Green
    "rgba(0, 128, 255, 0.5)", // Blue
    "rgba(255, 165, 0, 0.5)", // Orange
  ];

  // Define animation for the image's neon glow
  const imageNeonGlowAnimation = {
    filter: imageNeonColors.map(color => `drop-shadow(0 0 10px ${color}) drop-shadow(0 0 20px ${color})`),
  };

  const imageNeonGlowTransition = {
    duration: 25, // Very slow transition for color change
    ease: "easeInOut",
    repeat: Infinity,
    repeatType: "mirror", // Go back and forth through colors
  };

  const stats = [
    { icon: "https://res.cloudinary.com/dv1ald7mk/image/upload/v1754784251/certified_Logo_g1qyom.png", type: "image", value: "10,000+", label: "Satisfied Clients" },
    { icon: "https://res.cloudinary.com/dv1ald7mk/image/upload/v1754784543/Stock_Growth_Market_Graph_Dollar_Color.H02.2k_eqavyb.png", type: "image", value: "100+", label: "Average Score Increase", isLarger: true },
    { icon: "https://res.cloudinary.com/dv1ald7mk/image/upload/v1754784909/Five_Stars_Rating_White.H01.2k_uxeogm.png", type: "image", value: "5 Stars", label: "Client Rating", isEvenLarger: true } // Added isEvenLarger flag
  ];

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.8, delay: 0.6 }}
      className="relative bg-gray-800/50 backdrop-blur-lg rounded-3xl p-8 md:p-12 shadow-2xl border border-gray-700/50 mb-12 text-center"
    >
      <h2 className="text-3xl md:text-4xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-green-400 to-teal-600 mb-8">
        Real Results, Real Impact
      </h2>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
        {stats.map((stat, index) => (
          <motion.div
            key={index}
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6, delay: 0.7 + index * 0.2 }}
            className="relative bg-gray-900/50 p-6 rounded-2xl border border-gray-700/50 flex flex-col items-center justify-between h-full"
          >
            <div className="flex items-center justify-center mb-4 h-48">
              {stat.type === "image" ? (
                <motion.img 
                  src={stat.icon} 
                  alt={stat.label} 
                  className={
                    stat.isLarger 
                      ? "w-48 h-48 object-contain" 
                      : stat.isEvenLarger 
                        ? "w-48 h-48 object-contain" // Double size for this specific image
                        : "w-32 h-32 object-contain"
                  } 
                  animate={{ ...imageAnimation, ...imageNeonGlowAnimation }} // Combine animations
                  transition={{ ...imageTransition, ...imageNeonGlowTransition }} // Combine transitions
                />
              ) : (
                // If it's a Lucide icon, we can animate its color directly
                <motion.div
                  animate={{ color: imageNeonColors }} // Animate the color of the icon
                  transition={imageNeonGlowTransition}
                >
                  <stat.icon size={36} /> {/* Lucide icons use 'color' for stroke/fill */}
                </motion.div>
              )}
            </div>
            <div className="mt-auto">
              <h3 className="text-4xl font-bold text-white mb-2">{stat.value}</h3>
              <p className="text-gray-300 text-lg">{stat.label}</p>
            </div>
          </motion.div>
        ))}
      </div>
      <p className="text-xl text-gray-300 mb-8">
        Don't just take our word for it. Our clients' success stories speak volumes about the effectiveness of our program.
      </p>
      {/* You can add more testimonials or a link to a dedicated results page here */}
    </motion.div>
  );
};

export default ResultsSection;